import type {
  ContentLibraryInput,
  DynamicFieldDefinition,
  FormDSLContext,
} from '@commenda/types';
import { fetcher } from '@commenda/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createFormDataDependencyKey,
  resolveInputs,
} from './contentLibraryUtils';

interface ContentLibraryFieldsConfig {
  contentId: string;
  inputs?: ContentLibraryInput[];
}

interface UseContentLibraryFieldsParams {
  config: ContentLibraryFieldsConfig | undefined;
  dslContext: FormDSLContext | undefined;
  formData: Record<string, unknown>;
  skip?: boolean;
}

interface UseContentLibraryFieldsResult {
  fields: DynamicFieldDefinition[];
  metadata: Record<string, unknown> | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

interface ContentLibraryFieldsResponse {
  fields: Array<{
    key: string;
    label: string;
    type: string;
    required?: boolean;
    placeholder?: string;
    defaultValue?: unknown;
    options?: { value: string; label: string }[];
    displayOrder?: number;
    regex?: string;
    validationMessage?: string;
    groupKey?: string;
    disabled?: boolean;
    hidden?: boolean;
  }>;
  metadata?: Record<string, unknown>;
}

function mapToFieldDefinition(
  field: ContentLibraryFieldsResponse['fields'][0]
): DynamicFieldDefinition {
  const typeMapping: Record<string, DynamicFieldDefinition['type']> = {
    string: 'string',
    String: 'string',
    secureString: 'secureString',
    SecureString: 'secureString',
    number: 'number',
    Number: 'number',
    date: 'date',
    Date: 'date',
    select: 'select',
    Select: 'select',
    textarea: 'textarea',
    Textarea: 'textarea',
    email: 'email',
    Email: 'email',
    phone: 'phone',
    Phone: 'phone',
    url: 'url',
    Url: 'url',
  };

  return {
    key: field.key,
    label: field.label,
    type: typeMapping[field.type] || 'string',
    required: field.required,
    placeholder: field.placeholder,
    defaultValue: field.defaultValue,
    options: field.options,
    displayOrder: field.displayOrder,
    regex: field.regex,
    validationMessage: field.validationMessage,
    groupKey: field.groupKey,
    disabled: field.disabled,
    hidden: field.hidden,
  };
}

/**
 * Hook to fetch field definitions from Content Library for DynamicFields component.
 * Returns a standard DynamicFieldDefinition[] that can be rendered directly.
 */
export function useContentLibraryFields({
  config,
  dslContext,
  formData,
  skip = false,
}: UseContentLibraryFieldsParams): UseContentLibraryFieldsResult {
  const [fields, setFields] = useState<DynamicFieldDefinition[]>([]);
  const [metadata, setMetadata] = useState<Record<string, unknown> | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const lastFetchKey = useRef<string | null>(null);

  const formDataDependencyKey = createFormDataDependencyKey(
    config?.inputs,
    formData
  );

  const fetchFields = useCallback(async () => {
    if (!config?.contentId || skip) {
      console.log(
        '[useContentLibraryFields] Skipping - no contentId or skip=true',
        { contentId: config?.contentId, skip }
      );
      setFields([]);
      setMetadata(undefined);
      return;
    }

    console.log('[useContentLibraryFields] Resolving inputs:', {
      inputs: config.inputs,
      formData,
      dslContext,
    });

    const resolvedInputs = resolveInputs(config.inputs, dslContext, formData);

    if (resolvedInputs === null) {
      console.log(
        '[useContentLibraryFields] Missing required inputs - not fetching'
      );
      setFields([]);
      setMetadata(undefined);
      return;
    }

    console.log('[useContentLibraryFields] Resolved inputs:', resolvedInputs);

    const fetchKey = `${config.contentId}:${JSON.stringify(resolvedInputs)}`;

    if (fetchKey === lastFetchKey.current) {
      return;
    }

    lastFetchKey.current = fetchKey;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetcher(`content-library/${config.contentId}`, {
        method: 'POST',
        body: resolvedInputs,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            `Failed to fetch fields from content library: ${config.contentId}`
        );
      }

      const data: ContentLibraryFieldsResponse = await response.json();

      let mappedFields = (data.fields || [])
        .map(mapToFieldDefinition)
        .filter((f) => f.key && f.label && !f.hidden);

      mappedFields.sort(
        (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
      );

      setFields(mappedFields);
      setMetadata(data.metadata);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'));
      setFields([]);
      setMetadata(undefined);
    } finally {
      setIsLoading(false);
    }
  }, [config, dslContext, formData, skip]);

  const refetch = useCallback(() => {
    lastFetchKey.current = null;
    fetchFields();
  }, [fetchFields]);

  useEffect(() => {
    fetchFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config?.contentId,
    config?.inputs,
    dslContext,
    formDataDependencyKey,
    skip,
  ]);

  return {
    fields,
    metadata,
    isLoading,
    error,
    refetch,
  };
}
